/**
 * D-017 — podkład map w Leaflecie (jedyne miejsce, które tworzy kafle tła):
 * - `createConusBasemap` — `/` i `/us`: kafle USGS + nakładka wektorowa
 *   Natural Earth (granice, brzeg, miasta) w panelu NAD radarem;
 * - `addWorldBasemap` — `/explorer` i region EUROPA na `/`: NASA GIBS.
 * Konfiguracja (URL-e, atrybucje, reguła miast): `./config.ts`.
 */
import L from "leaflet";
import "./basemap.css";
import {
  CONUS_STYLES,
  EXPLORER_ATTRIBUTION_HTML,
  GIBS_STACK,
  VECTOR_FILES,
  selectCityLabels,
  type CityPoint,
  type ConusStyle,
  type ConusStyleId,
  type TileDef,
  type VectorSet,
  type VectorTheme,
} from "./config";

/** Panel nakładki wektorowej: nad radarem (overlayPane 400, pole GL 405), pod kontrolkami. */
export const VECTOR_PANE = "basemapLabels";
const VECTOR_PANE_Z = "450";

type VectorKind = keyof typeof VECTOR_FILES;
type GeoJsonFc = GeoJSON.FeatureCollection;

const vectorCache: Partial<Record<VectorKind, Promise<GeoJsonFc | null>>> = {};

function loadVector(kind: VectorKind): Promise<GeoJsonFc | null> {
  let p = vectorCache[kind];
  if (!p) {
    p = fetch(`/${VECTOR_FILES[kind]}`) // public/ → korzeń serwisu (jak /data/radar-sources/)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<GeoJsonFc>;
      })
      .catch((err: unknown) => {
        // Brak wektorów nie psuje mapy (kafle USGS dalej działają) — tylko ostrzeżenie.
        console.warn(`[basemap] nie wczytano ${VECTOR_FILES[kind]}:`, err);
        delete vectorCache[kind];
        return null;
      });
    vectorCache[kind] = p;
  }
  return p;
}

export function tileLayersFor(defs: TileDef[], maxZoom: number, attribution?: string): L.TileLayer[] {
  return defs.map((d, i) => {
    const opts: L.TileLayerOptions = { maxZoom };
    if (d.maxNativeZoom !== undefined) opts.maxNativeZoom = d.maxNativeZoom;
    if (d.className) opts.className = d.className;
    if (attribution && i === 0) opts.attribution = attribution;
    return L.tileLayer(d.url, opts);
  });
}

function ensurePane(map: L.Map, name: string, zIndex: string): HTMLElement {
  const existing = map.getPane(name);
  if (existing) return existing;
  const pane = map.createPane(name);
  pane.style.zIndex = zIndex;
  return pane;
}

// ---------- nakładka wektorowa ----------
const LINE_STYLES: Record<VectorTheme, Record<string, L.PathOptions>> = {
  light: {
    state: { color: "#334155", weight: 1, opacity: 0.6 },
    country: { color: "#1e293b", weight: 1.5, opacity: 0.7 },
    coast: { color: "#334155", weight: 1, opacity: 0.5 },
    lake: { color: "#334155", weight: 1, opacity: 0.5 },
  },
  dark: {
    state: { color: "#f1f5f9", weight: 1, opacity: 0.6 },
    country: { color: "#ffffff", weight: 1.5, opacity: 0.75 },
    coast: { color: "#e2e8f0", weight: 1, opacity: 0.55 },
    lake: { color: "#e2e8f0", weight: 1, opacity: 0.55 },
  },
};

function escHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);
}

/**
 * Granice/brzeg (L.geoJSON na wspólnym rendererze canvas) i nazwy miast
 * (L.marker z divIcon, widoczność wg rangi NE i zoomu, zachłannie bez
 * nakładania). Wszystko nieinteraktywne, w panelu `basemapLabels`.
 */
export class VectorOverlay {
  private readonly pane: HTMLElement;
  private readonly renderer: L.Canvas;
  private cfg: VectorSet | null = null;
  private borders: L.GeoJSON | null = null;
  private coast: L.GeoJSON | null = null;
  private cities: CityPoint[] | null = null;
  private readonly cityGroup: L.LayerGroup;
  private readonly cityMarkers = new Map<string, L.Marker>();
  private readonly selectionByZoom = new Map<number, Set<string>>();
  private readonly onZoomEnd = () => this.updateCities();
  private destroyed = false;

  constructor(private readonly map: L.Map) {
    this.pane = ensurePane(map, VECTOR_PANE, VECTOR_PANE_Z);
    this.pane.classList.add("bm-vector-pane");
    this.renderer = L.canvas({ pane: VECTOR_PANE, padding: 0.5 });
    this.cityGroup = L.layerGroup();
    map.on("zoomend", this.onZoomEnd);
  }

  /** null = ukryj całą nakładkę. */
  setConfig(cfg: VectorSet | null): void {
    this.cfg = cfg;
    this.pane.classList.toggle("bm-theme-dark", cfg?.theme === "dark");
    this.pane.classList.toggle("bm-theme-light", cfg?.theme !== "dark");
    if (cfg?.borders) void this.ensureLines("borders");
    if (cfg?.coast) void this.ensureLines("coast");
    if (cfg?.cities) void this.ensureCities();
    this.apply();
  }

  private styleFn(): (f?: GeoJSON.Feature) => L.PathOptions {
    const theme: VectorTheme = this.cfg?.theme ?? "light";
    return (f) => LINE_STYLES[theme][(f?.properties as { kind?: string } | null)?.kind ?? "state"] ?? LINE_STYLES[theme].state;
  }

  private async ensureLines(kind: "borders" | "coast"): Promise<void> {
    if ((kind === "borders" ? this.borders : this.coast) !== null) return;
    const fc = await loadVector(kind);
    if (!fc || this.destroyed || (kind === "borders" ? this.borders : this.coast) !== null) return;
    const layer = L.geoJSON(fc, { renderer: this.renderer, pane: VECTOR_PANE, interactive: false, style: this.styleFn() } as L.GeoJSONOptions);
    if (kind === "borders") this.borders = layer;
    else this.coast = layer;
    this.apply();
  }

  private async ensureCities(): Promise<void> {
    if (this.cities !== null) return;
    const fc = await loadVector("cities");
    if (!fc || this.destroyed || this.cities !== null) return;
    this.cities = fc.features
      .filter((f) => f.geometry?.type === "Point")
      .map((f) => {
        const [lon, lat] = (f.geometry as GeoJSON.Point).coordinates;
        const p = f.properties as { name: string; rank: number; pop_max: number };
        return { name: String(p.name), rank: Number(p.rank), pop_max: Number(p.pop_max), lat, lon };
      });
    this.apply();
  }

  private apply(): void {
    if (this.destroyed) return;
    const cfg = this.cfg;
    const style = this.styleFn();
    for (const [layer, on] of [
      [this.borders, !!cfg?.borders],
      [this.coast, !!cfg?.coast],
    ] as const) {
      if (!layer) continue;
      layer.setStyle(style);
      if (on && !this.map.hasLayer(layer)) layer.addTo(this.map);
      if (!on && this.map.hasLayer(layer)) layer.remove();
    }
    const citiesOn = !!cfg?.cities && this.cities !== null;
    if (citiesOn && !this.map.hasLayer(this.cityGroup)) this.cityGroup.addTo(this.map);
    if (!citiesOn && this.map.hasLayer(this.cityGroup)) this.cityGroup.remove();
    this.updateCities();
  }

  private markerFor(c: CityPoint): L.Marker {
    const key = `${c.name}|${c.lat}|${c.lon}`;
    let m = this.cityMarkers.get(key);
    if (!m) {
      m = L.marker([c.lat, c.lon], {
        pane: VECTOR_PANE,
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          className: "bm-city",
          html: `<span class="bm-city-dot" aria-hidden="true"></span><span class="bm-city-name" aria-hidden="true">${escHtml(c.name)}</span>`,
          iconSize: null as unknown as L.PointExpression,
          iconAnchor: [0, 0],
        }),
      });
      this.cityMarkers.set(key, m);
    }
    return m;
  }

  private updateCities(): void {
    if (!this.cities || !this.map.hasLayer(this.cityGroup)) return;
    const z = Math.round(this.map.getZoom());
    let sel = this.selectionByZoom.get(z);
    if (!sel) {
      const chosen = selectCityLabels(this.cities, z, (lat, lon) => this.map.project([lat, lon], z));
      sel = new Set(chosen.map((c) => `${c.name}|${c.lat}|${c.lon}`));
      this.selectionByZoom.set(z, sel);
    }
    for (const c of this.cities) {
      const key = `${c.name}|${c.lat}|${c.lon}`;
      const want = sel.has(key);
      const m = want ? this.markerFor(c) : this.cityMarkers.get(key);
      if (!m) continue;
      if (want && !this.cityGroup.hasLayer(m)) this.cityGroup.addLayer(m);
      if (!want && this.cityGroup.hasLayer(m)) this.cityGroup.removeLayer(m);
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.map.off("zoomend", this.onZoomEnd);
    this.borders?.remove();
    this.coast?.remove();
    this.cityGroup.remove();
  }
}

// ---------- CONUS ----------
export interface ConusBasemap {
  readonly style: ConusStyle;
  setStyle(id: ConusStyleId): ConusStyle;
  /** Pokazuje/ukrywa cały podkład (kafle + wektory) — np. przy zmianie regionu na `/`. */
  setVisible(on: boolean): void;
  destroy(): void;
}

export function createConusBasemap(
  map: L.Map,
  opts: { maxZoom: number; initial?: ConusStyleId; /** Atrybucja dla kontrolki Leafleta (jeśli mapa ją ma). */ attributionHtml?: string }
): ConusBasemap {
  const tilesByStyle = new Map<ConusStyleId, L.TileLayer[]>();
  const vectors = new VectorOverlay(map);
  let current: ConusStyle = CONUS_STYLES[opts.initial ?? "jasna"];
  let visible = true;
  let shown: L.TileLayer[] = [];

  const tilesFor = (id: ConusStyleId): L.TileLayer[] => {
    let t = tilesByStyle.get(id);
    if (!t) {
      t = tileLayersFor(CONUS_STYLES[id].tiles, opts.maxZoom, opts.attributionHtml);
      tilesByStyle.set(id, t);
    }
    return t;
  };

  const render = (): void => {
    for (const t of shown) t.remove();
    shown = visible ? tilesFor(current.id) : [];
    for (const t of shown) t.addTo(map);
    vectors.setConfig(visible ? current.vectors : null);
  };
  render();

  return {
    get style() {
      return current;
    },
    setStyle(id) {
      const next = CONUS_STYLES[id];
      if (next && next !== current) {
        current = next;
        render();
      }
      return current;
    },
    setVisible(on) {
      if (on !== visible) {
        visible = on;
        render();
      }
    },
    destroy() {
      for (const t of shown) t.remove();
      vectors.destroy();
    },
  };
}

// ---------- świat (GIBS) ----------
/**
 * Dodaje stos NASA GIBS. `ensureAttribution` (domyślnie tak): gdy mapa nie ma
 * kontrolki atrybucji, dokłada małą w prawym dolnym rogu — atrybucja OSM jest
 * wymagana licencją ODbL warstwy ląd/woda.
 */
export function addWorldBasemap(map: L.Map, opts: { maxZoom?: number; ensureAttribution?: boolean; attributionHtml?: string } = {}): L.TileLayer[] {
  if (opts.ensureAttribution !== false && !map.attributionControl) {
    map.attributionControl = L.control.attribution({ position: "bottomright", prefix: false }).addTo(map);
  }
  const layers = tileLayersFor(GIBS_STACK, opts.maxZoom ?? 19, opts.attributionHtml ?? EXPLORER_ATTRIBUTION_HTML);
  for (const l of layers) l.addTo(map);
  return layers;
}
